import { ReactNode, useEffect, useMemo, useState } from 'react';
import TextInputComponent from '../forms/GenericBaseForms/TextInputComponent/TextInputComponent';
import * as Yup from 'yup';
import { JobTypeKeys } from '../../mock/roleTypes';
import './ContactUsComponent.scss';
import { useFormik } from 'formik';
import { ButtonComponent } from '../UI/ButtonComponent/ButtonComponent';
import SelectFormComponent from '../forms/GenericBaseForms/SelectFormComponent/SelectFormComponent';
import { apolloClient } from '../../graphql/ApolloClient';
import { CREATE_NEW_CONTACT } from '../../graphql/mutations/contactUs';
import { ToastActions } from '../../redux/toast/ToastActions';
import { IToastState } from '../../common/view-model';
import { useAppDispatch } from '../../redux/store';

interface ContainerTwoColumnsI {
	classColumn1?: string;
	classColumn2?: string;
	FormComponent1: ReactNode;
	FormComponent2?: ReactNode;
}
export const ContainerTwoColumns = ({
	FormComponent1,
	FormComponent2,
	classColumn1,
	classColumn2
}: ContainerTwoColumnsI) => {
	return (
		<>
			<div className='contact-form-container'>
				<div className='contact-row-form'>
					<div className={`column-form ${classColumn1}`}>{FormComponent1}</div>
					<div className={`column-form ${classColumn2}`}>{FormComponent2}</div>
				</div>
			</div>
		</>
	);
};
const ContactUsSchema = Yup.object().shape({
	email: Yup.string()
		.email('Please enter a valid email address (example: name@example.com)')
		.matches(
			/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
			'Please enter a valid email address (example: name@example.com)'
		)
		.required('Email is required'),
	name: Yup.string()
		.test(
			'not-only-spaces',
			'Name cannot contain only spaces',

			value => {
				if (typeof value !== 'string') return false;
				return value.trim().length > 0;
			}
		)
		.min(2, 'Name is too short')
		.required('Name is required'),
	company: Yup.string()
		.test('not-only-spaces', 'Company name cannot contain only spaces', value => {
			if (typeof value !== 'string') return false;
			return value.trim().length > 0;
		})
		.min(2, 'Company name is too short')
		.required('Company name is required'),
	job_type: Yup.object(),
	question: Yup.string()
		.test('not-only-spaces', 'Question cannot contain only spaces', value => {
			if (typeof value !== 'string') return false;
			return value.trim().length > 0;
		})
		.required('Question is required')
});

export const ContactUsComponent = () => {
	const jobTypes = useMemo(() => {
		return Object.values(JobTypeKeys).map(jobName => ({
			id: jobName.toLowerCase(),
			name: jobName
		}));
	}, []);

	const dispatch = useAppDispatch();
	const setToast = (toastContent: IToastState) => dispatch(ToastActions.updateToast(toastContent));

	const [hasTouched, setHasTouched] = useState(false);
	const { errors, values, resetForm, handleChange, submitForm, setFieldValue } = useFormik({
		initialValues: {
			name: '',
			email: '',
			company: '',
			jobType: { id: '', name: '' },
			question: ''
		},
		onSubmit: async values => {
			await handleSubmitForm(values);
			resetForm();
		},
		validationSchema: ContactUsSchema
	});
	useEffect(() => {
		setHasTouched(false);
	}, [values]);

	const handleSubmitForm = async (values: any) => {
		try {
			await apolloClient.mutate({
				mutation: CREATE_NEW_CONTACT,
				variables: {
					input: {
						name: values?.name,
						email: values?.email,
						company: values?.company,
						job_type_id: values?.jobType?.id,
						job_type_name: values?.jobType?.name,
						question: values?.question
					}
				}
			});
			setToast({
				title: 'Success',
				message: '',
				type: 'success',
				position: 'top-right',
				closeOnClick: false,
				autoClose: 2000
			});
		} catch {
			setToast({
				title: 'Failed',
				message: '',
				type: 'error',
				position: 'top-right',
				closeOnClick: false,
				autoClose: 2000
			});
		}
	};

	return (
		<>
			<ContainerTwoColumns
				FormComponent1={
					<>
						<h3 className='contact-us-title'>Get in touch with us</h3>
						<span className='contact-us-description'>
							From checking in on the status of your uploaded products to getting on the Beacon
							Discovery platform, we’re here to help.
						</span>
						<div className='links-container'>
							<span>
								<a target='_blank' href='/getonbeacon' rel='noreferrer' className='contact-us-link'>
									Onboarding Help
								</a>
								<i className='ri-external-link-line'></i>
							</span>
							<span>
								<a target='_blank' href='/beaconFAQ' rel='noreferrer' className='contact-us-link'>
									FAQ
								</a>
								<i className='ri-external-link-line'></i>
							</span>
						</div>
					</>
				}
				classColumn1='contact-column-1'
				FormComponent2={
					<>
						<div className='form-input-container'>
							<p>Name*</p>
							<TextInputComponent
								name='name'
								placeholder='Name'
								error={errors.name?.length! > 0 && !errors.name?.includes('required')}
								onChange={handleChange}
								value={values.name || ''}
								errorHelperText={<>{errors.name}</>}
							/>
						</div>
						<div className='form-input-container'>
							<p>Email*</p>
							<TextInputComponent
								name='email'
								placeholder='Email'
								error={errors.email?.length! > 0 && !errors.email?.includes('required')}
								onChange={handleChange}
								value={values.email || ''}
								errorHelperText={<>{errors.email}</>}
							/>
						</div>
						<div className='form-input-container'>
							<p>Company*</p>
							<TextInputComponent
								name='company'
								placeholder='Company'
								error={errors.company?.length! > 0 && !errors.company?.includes('required')}
								onChange={handleChange}
								value={values.company || ''}
								errorHelperText={<>{errors.company}</>}
							/>
						</div>
						<div className='form-input-container'>
							<p>Are You A...*</p>
							<SelectFormComponent
								items={jobTypes}
								name='jobType'
								onChange={setFieldValue}
								value={values.jobType}
							></SelectFormComponent>
						</div>
						<div className='form-input-container'>
							<p>How Can We Help You?*</p>
							<TextInputComponent
								name='question'
								placeholder='How Can We Help You?'
								error={errors.question?.length! > 0 && !errors.question?.includes('required')}
								onChange={handleChange}
								type='textarea'
								value={values.question || ''}
								errorHelperText={<>{errors.question}</>}
							/>
						</div>{' '}
						<ButtonComponent
							onClick={() => {
								submitForm();
								setHasTouched(true);
							}}
							style='rounded-button primary'
							text='Submit'
						></ButtonComponent>
						{Object.keys(errors).length > 0 && hasTouched && (
							<p className='error-message'>All fields are required</p>
						)}
					</>
				}
				classColumn2='contact-column-2'
			></ContainerTwoColumns>
		</>
	);
};
